import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface RouterContextType {
  currentPath: string;
  navigate: (path: string) => void;
}

const RouterContext = createContext<RouterContextType>({
  currentPath: '/',
  navigate: () => {},
});

export const useRouter = () => useContext(RouterContext);

export const normalizePath = (rawPath: string): string => {
  if (!rawPath || rawPath === '/') return '/';
  // Strip trailing slashes: '/tools/' -> '/tools'
  const trimmed = rawPath.trim().replace(/\/+$/, '');
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

export const RouterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getInitialPath = (): string => {
    if (typeof window === 'undefined') return '/';
    // If visiting with an obsolete hash (#/tools, #/, etc.), auto-migrate to clean path (/tools, /)
    if (window.location.hash.startsWith('#/')) {
      const cleanPath = normalizePath(window.location.hash.slice(1)); // strips leading '#' -> '/...'
      try {
        window.history.replaceState(null, '', cleanPath);
      } catch {
        // fallback
      }
      return cleanPath;
    } else if (window.location.hash === '#') {
      try {
        window.history.replaceState(null, '', normalizePath(window.location.pathname));
      } catch {
        // fallback
      }
    }
    return normalizePath(window.location.pathname);
  };

  const [currentPath, setCurrentPath] = useState<string>(getInitialPath);

  const navigate = useCallback((targetPath: string) => {
    // In-page section anchor on home, e.g. /#pipeline or #pipeline
    if (targetPath.startsWith('#') || targetPath.startsWith('/#')) {
      const anchor = targetPath.replace(/^\/?#/, '');
      if (currentPath !== '/') {
        setCurrentPath('/');
        // Keep URL clean as '/' without polluting address bar with '#'
        try {
          window.history.pushState(null, '', '/');
        } catch {
          // fallback
        }
        setTimeout(() => {
          const el = document.getElementById(anchor) || document.querySelector(`#${anchor}`);
          el?.scrollIntoView({ behavior: 'smooth' });
        }, 120);
      } else {
        const el = document.getElementById(anchor) || document.querySelector(`#${anchor}`);
        el?.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    // Clean professional HTML5 page navigation (NO '#' in URL)
    const normalized = normalizePath(targetPath);
    setCurrentPath(normalized);
    try {
      window.history.pushState(null, '', normalized);
    } catch {
      // fallback
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentPath]);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(getInitialPath());
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <RouterContext.Provider value={{ currentPath, navigate }}>
      {children}
    </RouterContext.Provider>
  );
};

export default RouterProvider;
